// src/context/AuthContext.js
/**
 * Secure AuthContext - Uses Firebase Auth as the source of truth
 *
 * SECURITY: No sensitive user data is stored in localStorage.
 * Firebase Auth handles token persistence securely in IndexedDB.
 * User profile is fetched from Firestore on each app load.
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Fetches admin user profile from Firestore by Firebase UID
 * @param {string} firebaseUid - The Firebase Auth UID
 * @returns {Object|null} - User profile object or null if not found/not admin
 */
async function fetchAdminProfile(firebaseUid) {
  try {
    // Query users collection for admin with matching Firebase UID
    const adminQuery = query(
      collection(db, 'users'),
      where('uid', '==', firebaseUid),
      where('role', '==', 'Admin')
    );

    let adminSnapshot = await getDocs(adminQuery);

    // Fallback: If no user found by UID, try to find by checking auth.currentUser.email
    // This handles cases where UID wasn't stored in the user document
    if (adminSnapshot.empty && auth.currentUser?.email) {
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '==', auth.currentUser.email),
        where('role', '==', 'Admin')
      );
      adminSnapshot = await getDocs(emailQuery);
    }

    if (adminSnapshot.empty) {
      return null;
    }

    const userDoc = adminSnapshot.docs[0];
    const userData = userDoc.data();

    // Collect company IDs the admin manages.
    // Backward compatibility: older user docs only have `companyId` (single company).
    // Newer docs use `adminCompanyIds` array for multi-company admin support.
    const adminCompanyIds = userData.adminCompanyIds || [];
    if (adminCompanyIds.length === 0 && userData.companyId) {
      adminCompanyIds.push(userData.companyId);
    }

    if (adminCompanyIds.length === 0) {
      // No company access
      return null;
    }

    // Load company name for the primary company
    let companyName = '';
    if (userData.companyId) {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
        if (companyDoc.exists()) {
          companyName = companyDoc.data().name || '';
        }
      } catch (error) {
        // Error handled silently
      }
    }

    return {
      id: userDoc.id,
      uid: firebaseUid,
      email: userData.email,
      prename: userData.prename || '',
      lastname: userData.lastname || '',
      role: userData.role || 'Admin',
      companyId: userData.companyId || adminCompanyIds[0] || '',
      companyName: companyName,
      adminCompanyIds: adminCompanyIds,
      multiCompany: adminCompanyIds.length > 1,
      team: userData.team || '',
      status: userData.status || 'online'
    };
  } catch (error) {
    // Error handled silently
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is authenticated with Firebase - fetch their admin profile
        try {
          const adminProfile = await fetchAdminProfile(firebaseUser.uid);

          if (adminProfile) {
            setUser(adminProfile);
            setAuthError(null);
          } else {
            // User is authenticated but not an admin or has no company access
            // Sign them out
            await signOut(auth);
            setUser(null);
            setAuthError('no_admin_access');
          }
        } catch (error) {
          // Error handled silently
          setUser(null);
          setAuthError('profile_fetch_error');
        }
      } else {
        // User is not authenticated
        setUser(null);
        setAuthError(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  /**
   * Logout function - signs out from Firebase Auth
   * Firebase Auth will trigger onAuthStateChanged which clears the user state
   */
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setSessionExpired(false);
    } catch (error) {
      // Force clear user state even if signOut fails
      setUser(null);
    }
  }, []);

  /**
   * Update user data in context (for runtime updates like company name)
   * This does NOT persist to storage - only updates React state
   */
  const updateUser = useCallback((updates) => {
    setUser(currentUser => {
      if (!currentUser) return null;
      return {
        ...currentUser,
        ...updates,
        multiCompany: (updates.adminCompanyIds || currentUser.adminCompanyIds)?.length > 1
      };
    });
  }, []);

  /**
   * Force refresh user profile from Firestore
   * Useful after profile changes or to verify session
   */
  const refreshUserProfile = useCallback(async () => {
    if (!auth.currentUser) {
      setUser(null);
      return null;
    }

    try {
      const adminProfile = await fetchAdminProfile(auth.currentUser.uid);
      if (adminProfile) {
        setUser(adminProfile);
        return adminProfile;
      } else {
        await signOut(auth);
        setUser(null);
        return null;
      }
    } catch (error) {
      // Error handled silently
      return null;
    }
  }, []);

  /**
   * Clears the session expired flag (used after user acknowledges the message)
   */
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  /**
   * Mark session as expired (called from other parts of app if needed)
   */
  const markSessionExpired = useCallback(() => {
    setSessionExpired(true);
  }, []);

  // Value to provide in context
  const value = {
    user,
    setUser: updateUser,
    loading,
    logout,
    sessionExpired,
    clearSessionExpired,
    markSessionExpired,
    refreshUserProfile,
    authError,
    // Check if user is authenticated
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
